import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CertificateShare, CertificateQR, VerifiedBadge } from "@/components/CertificateShare";
import { Button } from "@/components/ui/button";
import { Award, X, Sparkles } from "lucide-react";

/**
 * Full-screen celebration modal — shown the moment a certificate is earned.
 * Prominent LinkedIn / X / share buttons make this a growth loop.
 */
export function CertificateCelebrationModal({ cert, onClose }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Prevent body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!cert) return null;

  return (
    <div
      data-testid="cert-celebration-modal"
      className={`fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur transition-opacity ${mounted ? "opacity-100" : "opacity-0"}`}
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Confetti-esque background rings */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full bg-primary/20 blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-1/3 h-40 w-40 rounded-full bg-accent/20 blur-2xl" />
      </div>

      <div className={`relative w-[min(94vw,640px)] max-h-[92vh] overflow-y-auto rounded-3xl border border-primary/30 bg-card p-6 md:p-8 shadow-2xl transition-transform ${mounted ? "scale-100" : "scale-95"}`}>
        <button
          data-testid="cert-celebration-close"
          onClick={onClose}
          className="absolute top-4 right-4 grid h-9 w-9 place-items-center rounded-full border border-border bg-background hover:border-primary transition"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Headline */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1">
            <Sparkles className="h-3.5 w-3.5 text-accent" />
            <span className="font-mono text-[0.65rem] uppercase tracking-widest text-accent">certificate earned</span>
          </div>
          <h2 className="mt-4 font-display text-3xl font-black tracking-tighter sm:text-4xl">Congrats — you did it.</h2>
          <p className="mt-2 text-sm text-muted-foreground">Your certificate is verifiable and yours forever.</p>
        </div>

        {/* Cert card */}
        <div className="mt-6 relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/10 to-accent/5 p-5">
          <div className="grid-pattern grid-pattern-fade absolute inset-0 opacity-30" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <VerifiedBadge />
              <div className="mt-3 label-mono text-primary">// certificate of completion</div>
              <div className="mt-1 text-xs text-muted-foreground">Awarded to</div>
              <div className="font-display text-xl font-black tracking-tighter truncate">{cert.user_name}</div>
              <div className="mt-2 text-xs text-muted-foreground">for successfully completing</div>
              <div className="font-display text-base font-bold">{cert.path_title}</div>
              <div className="mt-3 label-mono truncate">cert · {cert.cert_id}</div>
            </div>
            <CertificateQR cert={cert} size={80} />
          </div>
        </div>

        {/* Share buttons — the growth loop */}
        <div className="mt-6">
          <div className="label-mono mb-2">// share the win</div>
          <CertificateShare cert={cert} />
        </div>

        {/* Actions */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <Button
            data-testid="cert-celebration-view-all"
            asChild
            variant="outline"
            className="rounded-full font-mono text-xs uppercase tracking-widest"
          >
            <Link to="/app/certificates"><Award className="mr-2 h-3.5 w-3.5" /> View all certificates</Link>
          </Button>
          <Button
            data-testid="cert-celebration-continue"
            onClick={onClose}
            className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
          >
            Keep learning →
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CertificateCelebrationModal;
